import React from 'react';
import axios from 'axios';
import classNames from 'classnames';

import Icon from '../Icon/Icon';
import NoticeBanner from './NoticeBanner';
import useQueryState from 'Hooks/useQueryState';
import BannerMessageEntity from 'Models/Entities/BannerMessageEntity';
import { BannerVisibilityOptions, canDismiss, getVisibleBanners } from './Utils/bannerVisibility';
import { addDismissedBannerId, getDismissedBannerIds, pruneDismissedBannerIds } from './Utils/dismissedBanners';

/**
 * How many unpinned banners stay expanded. Pinned banners are always expanded on top of this, so the
 * limit caps the ordinary case (a handful of notices) without letting staff lose an urgent one.
 */
const DEFAULT_EXPANDED_LIMIT = 2;

interface NoticeBannerListProps extends Omit<BannerVisibilityOptions, 'dismissedIds'> {
	expandedLimit?: number;
	/** Applied to the stack wrapper, for surface-specific spacing. */
	className?: string;
	/**
	 * Optionally labels a banner with the part of the journey it applies to. Returning undefined — the
	 * default — leaves the banner unqualified, which is right whenever the notice covers everything the
	 * customer is looking at.
	 */
	scopeLabelFor?: (banner: BannerMessageEntity) => string | undefined;
}

/**
 * Fetches the banner messages once and renders the ones that apply to the given routes, dates and
 * surface. Shared by the ferry schedule page and the booking wizard, which differ only in where the
 * routes and dates come from.
 */
export default function NoticeBannerList({
	expandedLimit = DEFAULT_EXPANDED_LIMIT,
	className,
	scopeLabelFor,
	...options
}: NoticeBannerListProps) {
	const { state, query } = useQueryState(async () => {
		const res = await axios.get<BannerMessageEntity[]>('/api/entity/BannerMessageEntity/messages');
		return (res.data ?? []).map(row => new BannerMessageEntity(row));
	});

	React.useEffect(() => {
		query();
	}, []);

	if (state.status !== 'success' || state.data.length === 0) {
		return <></>;
	}

	return (
		<Body
			bannerMessages={state.data}
			expandedLimit={expandedLimit}
			className={className}
			scopeLabelFor={scopeLabelFor}
			options={options}
		/>
	);
}

interface BodyProps {
	bannerMessages: BannerMessageEntity[];
	expandedLimit: number;
	className?: string;
	scopeLabelFor?: (banner: BannerMessageEntity) => string | undefined;
	options: Omit<BannerVisibilityOptions, 'dismissedIds'>;
}

function Body({ bannerMessages, expandedLimit, className, scopeLabelFor, options }: BodyProps) {
	const [dismissedIds, setDismissedIds] = React.useState<string[]>(() => getDismissedBannerIds());
	const [showAll, setShowAll] = React.useState(false);

	// Housekeeping: a dismissal for a banner that no longer exists can never be used again, so it is
	// dropped rather than left to accumulate in the customer's browser.
	React.useEffect(() => {
		pruneDismissedBannerIds(bannerMessages.map(banner => banner.id));
	}, [bannerMessages]);

	// Not memoised: it is a handful of filters over a handful of banners, and every input (the routes,
	// the viewed dates) changes as the customer moves around the page anyway.
	const visible = getVisibleBanners(bannerMessages, { ...options, dismissedIds });

	const handleDismiss = (id: string) => setDismissedIds(addDismissedBannerId(id));

	if (visible.length === 0) {
		return <></>;
	}

	// Pinned banners don't spend any of the expanded budget — they are always shown in full.
	const pinnedCount = visible.filter(banner => banner.pinned).length;
	const hasOverflow = visible.length > pinnedCount + expandedLimit;
	const expanded = showAll || !hasOverflow ? visible : visible.slice(0, pinnedCount + expandedLimit);
	const collapsedCount = visible.length - expanded.length;

	const renderBanner = (banner: BannerMessageEntity) => (
		<NoticeBanner
			key={banner.id}
			text={banner.text}
			title={banner.title}
			scope={scopeLabelFor?.(banner)}
			colour={banner.color}
			icon={banner.icon}
			onDismiss={canDismiss(banner) ? () => handleDismiss(banner.id) : undefined}
		/>
	);

	return (
		<div className={classNames('notice-banner-stack', className)}>
			{expanded.map(renderBanner)}
			{hasOverflow && (
				<div className="notice-banner-more">
					<button
						type="button"
						className="notice-banner-more__toggle"
						onClick={() => setShowAll(!showAll)}
						aria-expanded={showAll}
						data-test-id="toggle-more-notices"
					>
						<Icon name={showAll ? 'expand_less' : 'expand_more'} useMaterialIcons />
						{showAll
							? 'Show fewer notices'
							: `${collapsedCount} more ${collapsedCount === 1 ? 'notice' : 'notices'}`}
					</button>
				</div>
			)}
		</div>
	);
}
